import React, { useState, useEffect, useCallback } from "react";
import api, { isOrganizationScopeCancel, settleOrganizationScopeCancel } from "../lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  Plus, Edit2, Trash2, Send, Eye, Sparkles, FileText, X,
  AlertTriangle, AlertCircle, Info, Calendar, Building2, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";


const BULLETIN_TYPES = [
  { value: "regulatory_change", label: "Regulatory Change", labelCs: "Regulatorní změna" },
  { value: "template_update", label: "Template Update", labelCs: "Aktualizace šablony" },
  { value: "system_info", label: "System Info", labelCs: "Systémová informace" },
];

const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical", labelCs: "Kritická", color: "bg-red-500" },
  { value: "important", label: "Important", labelCs: "Důležitá", color: "bg-amber-500" },
  { value: "informational", label: "Informational", labelCs: "Informační", color: "bg-blue-500" },
];

export default function BulletinManagement() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [organizations, setOrganizations] = useState([]);
  const [kbDocuments, setKbDocuments] = useState([]);
  
  // Modal states
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  
  // Form state
  const [editingBulletin, setEditingBulletin] = useState(null);
  const [formData, setFormData] = useState({
    type: "regulatory_change",
    severity: "important",
    title: "",
    content: "",
    effective_date: "",
    org_ids: [],
    related_kb_doc_id: "",
  });
  const [recipientMode, setRecipientMode] = useState("all"); // all | selected — kept separate from formData.org_ids so the Select doesn't snap back to "all" while no org is checked yet
  const [contentMode, setContentMode] = useState("edit"); // edit | preview
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedBulletinId, setSelectedBulletinId] = useState(null);

  const fetchBulletins = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const res = await api.get(`/bulletins?${params}`, {
        
      });
      setBulletins(res.data.bulletins || []);
    } catch (err) {
      toast.error(t.bulletins?.errorLoading || t.errors?.failedToLoadBulletins || "Failed to load bulletins");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const res = await api.get(`/organizations`, {
        
      });
      setOrganizations(res.data || []);
    } catch (err) {
      console.error("Failed to load organizations:", err);
    }
  }, [getAuthHeaders]);

  const fetchKbDocuments = useCallback(async () => {
    try {
      const res = await api.get(`/compliance/knowledge-base`, {
        
      });
      setKbDocuments(res.data || []);
    } catch (err) {
      console.error("Failed to load KB documents:", err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchBulletins();
    fetchOrganizations();
    fetchKbDocuments();
  }, [fetchBulletins, fetchOrganizations, fetchKbDocuments]);

  const openNewBulletin = () => {
    setEditingBulletin(null);
    setFormData({
      type: "regulatory_change",
      severity: "important",
      title: "",
      content: "",
      effective_date: new Date().toISOString().split("T")[0],
      org_ids: [],
      related_kb_doc_id: "",
    });
    setRecipientMode("all");
    setContentMode("edit");
    setShowEditor(true);
  };

  const openEditBulletin = (bulletin) => {
    setEditingBulletin(bulletin);
    setFormData({
      type: bulletin.type,
      severity: bulletin.severity,
      title: bulletin.title,
      content: bulletin.content,
      effective_date: bulletin.effective_date.split("T")[0],
      org_ids: bulletin.org_ids || [],
      related_kb_doc_id: bulletin.related_kb_doc_id || "",
    });
    setRecipientMode((bulletin.org_ids || []).length > 0 ? "selected" : "all");
    setContentMode("edit");
    setShowEditor(true);
  };

  // Guards against saving/generating a bulletin with "Selected Organizations" chosen but no
  // organization actually checked — the backend treats an empty org_ids array as "all organizations",
  // so an unchecked "selected" mode would silently broadcast to everyone instead of no one.
  const hasValidRecipients = () => {
    if (recipientMode === "selected" && formData.org_ids.length === 0) {
      toast.error(t.bulletins?.selectOrgRequired || "Select at least one organization");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error(t.bulletins?.titleRequired || "Title is required");
      return;
    }
    if (!formData.content.trim()) {
      toast.error(t.bulletins?.contentRequired || "Content is required");
      return;
    }
    if (!hasValidRecipients()) {
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        effective_date: new Date(formData.effective_date).toISOString(),
        related_kb_doc_id: formData.related_kb_doc_id || null,
      };

      if (editingBulletin) {
        await api.put(`/bulletins/${editingBulletin.id}`, payload, settleOrganizationScopeCancel());
        toast.success(t.bulletins?.updated || "Bulletin updated");
      } else {
        await api.post(`/bulletins`, payload, settleOrganizationScopeCancel());
        toast.success(t.bulletins?.created || "Bulletin created");
      }

      setShowEditor(false);
      fetchBulletins();
    } catch (err) {
      if (isOrganizationScopeCancel(err)) return;
      toast.error(err.response?.data?.detail || t.bulletins?.saveError || "Failed to save bulletin");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!formData.related_kb_doc_id) {
      toast.error(t.bulletins?.selectKbDocFirst || "Select a KB document first");
      return;
    }
    if (!hasValidRecipients()) {
      return;
    }

    // Need to create bulletin first if new
    let bulletinId = editingBulletin?.id;
    
    try {
      setGenerating(true);
      
      if (!bulletinId) {
        // Create draft bulletin first
        const createRes = await api.post(`/bulletins`, {
          ...formData,
          effective_date: new Date(formData.effective_date || new Date()).toISOString(),
          title: formData.title || "Draft",
          content: formData.content || "Generating...",
        }, settleOrganizationScopeCancel());
        bulletinId = createRes.data.id;
        setEditingBulletin(createRes.data);
      }

      const res = await api.post(
        `/bulletins/${bulletinId}/generate-ai`,
        { kb_document_id: formData.related_kb_doc_id },
        settleOrganizationScopeCancel()
      );

      setFormData((prev) => ({
        ...prev,
        title: res.data.title,
        content: res.data.content,
        severity: res.data.severity_suggestion,
      }));

      toast.success(t.bulletins?.aiGenerated || "AI generated content - review and edit as needed");
    } catch (err) {
      if (isOrganizationScopeCancel(err)) return;
      toast.error(err.response?.data?.detail || t.bulletins?.aiGenerateError || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBulletinId) return;
    
    try {
      await api.delete(`/bulletins/${selectedBulletinId}`, {
        
      });
      toast.success(t.bulletins?.deleted || "Bulletin deleted");
      setShowDeleteConfirm(false);
      fetchBulletins();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.bulletins?.deleteError || "Failed to delete");
    }
  };

  const handleSend = async () => {
    if (!selectedBulletinId) return;
    
    try {
      await api.post(`/bulletins/${selectedBulletinId}/send`, {}, {
        
      });
      toast.success(t.bulletins?.sent || "Bulletin sent to all recipients");
      setShowSendConfirm(false);
      fetchBulletins();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.bulletins?.sendError || "Failed to send");
    }
  };

  const getSeverityBadge = (severity) => {
    const opt = SEVERITY_OPTIONS.find((s) => s.value === severity);
    return (
      <Badge className={`${opt?.color || "bg-gray-500"} text-white`}>
        {severity === "critical" && <AlertTriangle className="w-3 h-3 mr-1" />}
        {severity === "important" && <AlertCircle className="w-3 h-3 mr-1" />}
        {severity === "informational" && <Info className="w-3 h-3 mr-1" />}
        {opt?.label || severity}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    return status === "sent" ? (
      <Badge className="bg-green-500 text-white">Sent</Badge>
    ) : (
      <Badge variant="outline">Draft</Badge>
    );
  };

  const getTypeBadge = (type) => {
    const opt = BULLETIN_TYPES.find((t) => t.value === type);
    return <Badge variant="secondary">{opt?.label || type}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="bulletin-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            {t.bulletins?.title || "Compliance Bulletins"}
          </h1>
          <p className="text-[var(--text-muted)]">
            {t.bulletins?.subtitle || "Create and manage regulatory bulletins for operators"}
          </p>
        </div>
        <Button onClick={openNewBulletin} data-testid="create-bulletin-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t.bulletins?.newBulletin || "New Bulletin"}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="all">{t.bulletins?.tabs?.all || "All"}</TabsTrigger>
            <TabsTrigger value="draft">{t.bulletins?.tabs?.drafts || "Drafts"}</TabsTrigger>
            <TabsTrigger value="sent">{t.bulletins?.tabs?.sent || "Sent"}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Bulletins List */}
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--text-muted)]" />
          </div>
        ) : bulletins.length === 0 ? (
          <div className="p-8 text-center text-[var(--text-muted)]">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{t.common?.noData || "No bulletins found"}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[var(--bg-secondary)]">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-muted)]">{t.bulletins?.columns?.title || "Title"}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-muted)]">{t.bulletins?.columns?.type || "Type"}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-muted)]">{t.bulletins?.columns?.severity || "Severity"}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-muted)]">{t.bulletins?.columns?.status || "Status"}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-muted)]">{t.bulletins?.columns?.effective || "Effective"}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-muted)]">{t.bulletins?.columns?.recipients || "Recipients"}</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-[var(--text-muted)]">{t.bulletins?.columns?.actions || "Actions"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--card-border)]">
              {bulletins.map((bulletin) => (
                <tr key={bulletin.id} className="hover:bg-[var(--bg-secondary)]/50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--text-primary)]">{bulletin.title}</div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {t.bulletins?.attributionBy || "by"} {bulletin.created_by_name || "Unknown"}
                    </div>
                  </td>
                  <td className="px-4 py-3">{getTypeBadge(bulletin.type)}</td>
                  <td className="px-4 py-3">{getSeverityBadge(bulletin.severity)}</td>
                  <td className="px-4 py-3">{getStatusBadge(bulletin.status)}</td>
                  <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                    {new Date(bulletin.effective_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                    {bulletin.org_ids?.length === 0 ? "All" : `${bulletin.org_ids.length} orgs`}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingBulletin(bulletin);
                          setShowPreview(true);
                        }}
                        data-testid={`preview-bulletin-${bulletin.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {bulletin.status === "draft" && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditBulletin(bulletin)}
                            data-testid={`edit-bulletin-${bulletin.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => {
                              setSelectedBulletinId(bulletin.id);
                              setShowSendConfirm(true);
                            }}
                            data-testid={`send-bulletin-${bulletin.id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedBulletinId(bulletin.id);
                              setShowDeleteConfirm(true);
                            }}
                            data-testid={`delete-bulletin-${bulletin.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBulletin ? "Edit Bulletin" : "Create Bulletin"}
            </DialogTitle>
            <DialogDescription>
              Create regulatory bulletins to notify operators of important changes
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Type & Severity Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData((p) => ({ ...p, type: v }))}
                >
                  <SelectTrigger data-testid="bulletin-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BULLETIN_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(v) => setFormData((p) => ({ ...p, severity: v }))}
                >
                  <SelectTrigger data-testid="bulletin-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${s.color}`} />
                          {s.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                placeholder="Enter bulletin title..."
                data-testid="bulletin-title-input"
              />
            </div>

            {/* Effective Date */}
            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData((p) => ({ ...p, effective_date: e.target.value }))}
                data-testid="bulletin-date-input"
              />
            </div>

            {/* KB Document & AI Generate */}
            <div className="space-y-2">
              <Label>Related KB Document (for AI generation)</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.related_kb_doc_id}
                  onValueChange={(v) => setFormData((p) => ({ ...p, related_kb_doc_id: v }))}
                >
                  <SelectTrigger className="flex-1" data-testid="bulletin-kb-doc-select">
                    <SelectValue placeholder="Select KB document..." />
                  </SelectTrigger>
                  <SelectContent>
                    {kbDocuments.map((doc) => (
                      <SelectItem key={doc.id} value={doc.id}>
                        {doc.filename}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={handleGenerateAI}
                  disabled={generating || !formData.related_kb_doc_id}
                  data-testid="generate-ai-btn"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate with AI
                </Button>
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-2">
              <Label>Recipients</Label>
              <Select
                value={recipientMode}
                onValueChange={(v) => {
                  setRecipientMode(v);
                  if (v === "all") {
                    setFormData((p) => ({ ...p, org_ids: [] }));
                  }
                }}
              >
                <SelectTrigger data-testid="bulletin-recipients-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  <SelectItem value="selected">Selected Organizations</SelectItem>
                </SelectContent>
              </Select>
              {recipientMode === "selected" && (
                <div
                  className="space-y-1.5 rounded-md border border-[var(--border-default)] p-2 max-h-40 overflow-y-auto"
                  data-testid="bulletin-recipients-org-list"
                >
                  {organizations.length === 0 ? (
                    <p className="text-sm text-[var(--text-muted)]">No organizations available</p>
                  ) : (
                    organizations.map((org) => (
                      <label key={org.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={formData.org_ids.includes(org.id)}
                          onCheckedChange={(checked) => {
                            setFormData((p) => ({
                              ...p,
                              org_ids: checked
                                ? [...p.org_ids, org.id]
                                : p.org_ids.filter((id) => id !== org.id),
                            }));
                          }}
                        />
                        {org.company_name}
                      </label>
                    ))
                  )}
                  {formData.org_ids.length > 0 && (
                    <div className="text-xs text-[var(--text-muted)] pt-1">
                      {formData.org_ids.length} organizations selected
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Content Editor */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Content (Markdown)</Label>
                <Tabs value={contentMode} onValueChange={setContentMode}>
                  <TabsList className="h-8">
                    <TabsTrigger value="edit" className="text-xs px-3">Edit</TabsTrigger>
                    <TabsTrigger value="preview" className="text-xs px-3">Preview</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              {contentMode === "edit" ? (
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData((p) => ({ ...p, content: e.target.value }))}
                  placeholder="Write bulletin content in Markdown..."
                  className="min-h-[250px] font-mono text-sm"
                  data-testid="bulletin-content-textarea"
                />
              ) : (
                <div className="min-h-[250px] p-4 border rounded-md bg-[var(--bg-secondary)] prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{formData.content || "*No content*"}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-bulletin-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingBulletin ? "Update" : "Create"} Bulletin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 flex-wrap">
              {editingBulletin && getSeverityBadge(editingBulletin.severity)}
              {editingBulletin && getTypeBadge(editingBulletin.type)}
              {editingBulletin && getStatusBadge(editingBulletin.status)}
            </div>
            <DialogTitle className="text-xl mt-2">
              {editingBulletin?.title}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Effective: {editingBulletin && new Date(editingBulletin.effective_date).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {editingBulletin?.org_ids?.length === 0 ? "All orgs" : `${editingBulletin?.org_ids?.length} orgs`}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="prose prose-sm dark:prose-invert max-w-none py-4">
            <ReactMarkdown>{editingBulletin?.content || ""}</ReactMarkdown>
          </div>

          {editingBulletin?.related_kb_doc_filename && (
            <div className="flex items-center gap-2 p-3 bg-[var(--bg-secondary)] rounded-lg">
              <FileText className="w-4 h-4 text-[var(--text-muted)]" />
              <span className="text-sm">
                Related: {editingBulletin.related_kb_doc_filename}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            {editingBulletin?.status === "draft" && (
              <Button
                onClick={() => {
                  setShowPreview(false);
                  setSelectedBulletinId(editingBulletin.id);
                  setShowSendConfirm(true);
                }}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Bulletin
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bulletin?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The bulletin draft will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Confirmation */}
      <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Bulletin?</AlertDialogTitle>
            <AlertDialogDescription>
              This will publish the bulletin to all recipients. They will receive in-app notifications.
              Sent bulletins cannot be edited or deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSend} className="bg-green-600 hover:bg-green-700">
              <Send className="w-4 h-4 mr-2" />
              Send Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
